import { HttpErrorResponse } from '@angular/common/http';
import type {
  AdminSystemUser,
  AdminTenantSummary,
  UserTenantAssignment,
  VaultTenantRole,
} from '../../../../domain/src/lib/admin-users/admin-user.model';
import {
  resolveAdminUserTenantAssignmentForDisplay,
} from '../../../../data-access/src/lib/admin-users/admin-user-normalize.util';
import {
  normalizeVaultRoleToken,
  isSimappeTechnicalRoleToken,
  emailIsCanonicalVaultSuperadmin,
  resolveCanonicalVaultSuperadminEmail,
  readSessionUserEmail,
  vaultCurrentTenantIdFromSession,
  vaultCurrentUserIdFromSession,
  vaultResolveAdminUsersActor,
  vaultResolveAdminUsersActorFromDashboard,
  vaultResolveIsSuperadmin,
  vaultUserIsAdminEmpresaFromSession,
  vaultUserIsSuperadminFromSession,
  deriveSimappeLoginUsername,
  buildVaultUserLoginHintFromAccessUsername,
  resolveAdminUserAccessUsername,
  SIMAPPE_PASSWORD_MIN_LENGTH,
  SIMAPPE_PASSWORD_MAX_LENGTH,
  type VaultAdminUsersActor,
} from '@nebula/shared-vault/data-access';

export type { VaultAdminUsersActor };

export {
  vaultCurrentTenantIdFromSession,
  vaultCurrentUserIdFromSession,
  vaultResolveAdminUsersActor,
  vaultResolveAdminUsersActorFromDashboard,
  vaultResolveIsSuperadmin,
  vaultUserIsAdminEmpresaFromSession,
  vaultUserIsSuperadminFromSession,
};

/** @deprecated Usar `vaultUserIsSuperadminFromSession`. */
export const vaultUserIsSuperadminFromLocalStorage = vaultUserIsSuperadminFromSession;

/** @deprecated Usar `vaultUserIsAdminEmpresaFromSession`. */
export const vaultUserIsAdminEmpresaFromLocalStorage = vaultUserIsAdminEmpresaFromSession;

/** @deprecated Usar `vaultCurrentTenantIdFromSession`. */
export const vaultCurrentTenantIdFromLocalStorage = vaultCurrentTenantIdFromSession;

/** @deprecated Usar `vaultCurrentUserIdFromSession`. */
export const vaultCurrentUserIdFromLocalStorage = vaultCurrentUserIdFromSession;

/** @deprecated Usar `vaultResolveAdminUsersActorFromDashboard`. */
export const resolveActorFromDashboardRole = vaultResolveAdminUsersActorFromDashboard;

export const VAULT_TENANT_ROLE_OPTIONS: Array<{ label: string; value: VaultTenantRole }> = [
  { label: 'Administrador', value: 'admin_empresa' },
  { label: 'Archivista', value: 'archivista' },
  { label: 'Auditor', value: 'auditor' },
  { label: 'Consulta', value: 'consulta' },
];

export const VAULT_TENANT_ROLE_LABELS: Record<string, string> = {
  admin_empresa: 'Administrador',
  archivista: 'Archivista',
  auditor: 'Auditor',
  consulta: 'Consulta',
  superadmin: 'Superadmin',
};

/** Roles que un administrador de empresa puede asignar en su tenant (sin superadmin global). */
export const VAULT_TENANT_ROLES_ASSIGNABLE_BY_ADMIN_EMPRESA: VaultTenantRole[] =
  VAULT_TENANT_ROLE_OPTIONS.map((o) => o.value);

/** Fallback cuando el API legacy no expone `GET /api/admin/user-tenants/user/{id}`. */
export function mapEmbeddedUserTenantAssignments(
  user: Pick<AdminSystemUser, 'id' | 'tenants'>,
): UserTenantAssignment[] {
  return (user.tenants ?? []).map((t) => ({
    id: t.id || `${user.id}-${t.tenant_id}`,
    user_id: user.id,
    tenant_id: t.tenant_id,
    role: t.role,
    tenant_name: t.tenant_name,
    tenant_slug: t.tenant_slug,
  }));
}

export function roleOptionsForActor(actor: VaultAdminUsersActor): Array<{ label: string; value: string }> {
  if (actor === 'superadmin') {
    return VAULT_TENANT_ROLE_OPTIONS.map((o) => ({ label: o.label, value: o.value }));
  }
  if (actor === 'admin_empresa') {
    return VAULT_TENANT_ROLE_OPTIONS.filter((o) =>
      VAULT_TENANT_ROLES_ASSIGNABLE_BY_ADMIN_EMPRESA.includes(o.value),
    ).map((o) => ({ label: o.label, value: o.value }));
  }
  return [{ label: 'Consulta', value: 'consulta' }];
}

export function tenantRoleLabel(role: string): string {
  return VAULT_TENANT_ROLE_LABELS[normalizeVaultRoleToken(role)] ?? role;
}

/** Rol Vault para UI: ignora ROLE_DEVELOPER de Simappe (solo login técnico). */
export function displayVaultTenantRole(role: string | null | undefined): string {
  const trimmed = (role ?? '').trim();
  if (!trimmed || isSimappeTechnicalRoleToken(trimmed)) return '';
  return tenantRoleLabel(trimmed);
}

const ADMIN_USER_UNASSIGNED_LABEL = 'Sin asignar';

function resolveAdminUserPrimaryAssignment(
  user: AdminSystemUser,
  catalog?: Array<Pick<AdminTenantSummary, 'id' | 'name'>>,
) {
  if (user.is_superadmin) return null;
  return resolveAdminUserTenantAssignmentForDisplay(user, catalog);
}

/** Empresa disponible para asignar al crear o reasignar un usuario. */
export function isAdminTenantAssignable(
  tenant: Pick<AdminTenantSummary, 'is_active'>,
): boolean {
  return tenant.is_active !== false;
}

export function filterAssignableAdminTenants<T extends Pick<AdminTenantSummary, 'is_active'>>(
  tenants: T[],
): T[] {
  return tenants.filter(isAdminTenantAssignable);
}

/** Devuelve el tenant solo si sigue activo; las inactivas no aparecen en selectores de asignación. */
export function resolveAssignableAdminUserTenantId(
  tenantId: string | null | undefined,
  catalog: Array<Pick<AdminTenantSummary, 'id' | 'is_active'>>,
): string {
  const id = tenantId?.trim() ?? '';
  if (!id) return '';
  const tenant = catalog.find((t) => t.id === id);
  return tenant && isAdminTenantAssignable(tenant) ? id : '';
}

export function formatAdminUserCompanySummary(
  user: AdminSystemUser,
  catalog?: Array<Pick<AdminTenantSummary, 'id' | 'name'>>,
): string {
  if (user.is_superadmin) {
    const t = user.tenants?.[0];
    return t?.tenant_name?.trim() || t?.tenant_id?.trim() || 'Global';
  }
  const primary = resolveAdminUserPrimaryAssignment(user, catalog);
  if (!primary?.tenant_id?.trim()) return ADMIN_USER_UNASSIGNED_LABEL;
  return primary.tenant_name?.trim() || primary.tenant_id.trim();
}

export function formatAdminUserRoleSummary(
  user: AdminSystemUser,
  catalog?: Array<Pick<AdminTenantSummary, 'id' | 'name'>>,
): string {
  if (user.is_superadmin) return 'Superadmin';
  const primary = resolveAdminUserPrimaryAssignment(user, catalog);
  if (!primary) return ADMIN_USER_UNASSIGNED_LABEL;
  return (
    displayVaultTenantRole(primary.role) ||
    tenantRoleLabel(primary.role) ||
    primary.role?.trim() ||
    ADMIN_USER_UNASSIGNED_LABEL
  );
}

export function formatAdminUserRolesSummary(
  user: AdminSystemUser,
  catalog?: Array<Pick<AdminTenantSummary, 'id' | 'name'>>,
): string {
  if (user.is_superadmin) return 'Superadmin';
  const company = formatAdminUserCompanySummary(user, catalog);
  const role = formatAdminUserRoleSummary(user, catalog);
  if (company === ADMIN_USER_UNASSIGNED_LABEL && role === ADMIN_USER_UNASSIGNED_LABEL) {
    return ADMIN_USER_UNASSIGNED_LABEL;
  }
  if (role === ADMIN_USER_UNASSIGNED_LABEL || role === 'Superadmin') return company;
  return `${company}: ${role}`;
}

export function validateAdminUserFormEmail(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return 'El correo electrónico es obligatorio.';
  if (!trimmed.includes('@')) {
    return 'Falta @ en el correo. Ejemplo: usuario@dominio.com';
  }
  const at = trimmed.indexOf('@');
  const local = trimmed.slice(0, at).trim();
  const domain = trimmed.slice(at + 1).trim();
  if (!local) return 'Ingrese la parte antes de @.';
  if (!domain || !domain.includes('.')) {
    return 'Ingrese un dominio válido después de @.';
  }
  return null;
}

function extractHttpErrorDetail(err: HttpErrorResponse): string {
  const body = err.error as unknown;
  if (typeof body === 'string' && body.trim()) return body.trim();
  if (typeof body !== 'object' || body === null) return '';
  const record = body as Record<string, unknown>;
  const detail = record['detail'] ?? record['message'] ?? record['error'];
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (!item || typeof item !== 'object') return '';
        const row = item as Record<string, unknown>;
        const loc = Array.isArray(row['loc']) ? row['loc'].join('.') : '';
        const msg = typeof row['msg'] === 'string' ? row['msg'] : '';
        if (!msg) return '';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(' | ');
  }
  return '';
}

export function resolveAdminUserEmailApiError(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const detail = extractHttpErrorDetail(err);
  const lower = detail.toLowerCase();
  if (
    err.status === 400 &&
    lower.includes('email') &&
    (lower.includes('registr') || lower.includes('exist') || lower.includes('duplicad') || lower.includes('ya está'))
  ) {
    return detail.trim() || 'El email ya está registrado.';
  }
  if (
    lower.includes('body.email') ||
    (lower.includes('email') &&
      (lower.includes('inválido') || lower.includes('invalid') || lower.includes('value error')))
  ) {
    return 'Ingrese un correo válido. Debe incluir @ y dominio (ej. usuario@dominio.com).';
  }
  return null;
}

export type AdminCreatePasswordRuleState = 'pending' | 'met' | 'unmet';

export function resolveAdminCreatePasswordMinRule(length: number): AdminCreatePasswordRuleState {
  if (length <= 0) return 'pending';
  return length >= SIMAPPE_PASSWORD_MIN_LENGTH ? 'met' : 'unmet';
}

export function resolveAdminCreatePasswordMaxRule(length: number): AdminCreatePasswordRuleState {
  if (length <= 0) return 'pending';
  return length <= SIMAPPE_PASSWORD_MAX_LENGTH ? 'met' : 'unmet';
}

export function areAdminSimappePasswordRulesMet(length: number): boolean {
  if (length <= 0) return false;
  return (
    resolveAdminCreatePasswordMinRule(length) === 'met' &&
    resolveAdminCreatePasswordMaxRule(length) === 'met'
  );
}

export function doesAdminSimappePasswordBlockSave(length: number): boolean {
  if (length <= 0) return false;
  return !areAdminSimappePasswordRulesMet(length);
}

export function findAdminUserEmailConflict(
  users: AdminSystemUser[],
  email: string,
): AdminSystemUser | null {
  const token = normalizeAdminUserEmail(email);
  if (!token) return null;
  return users.find((user) => normalizeAdminUserEmail(user.email) === token) ?? null;
}

export function findAdminUserLoginUsernameConflict(
  users: AdminSystemUser[],
  email: string,
): AdminSystemUser | null {
  const login = deriveSimappeLoginUsername(email).trim().toLowerCase();
  const emailToken = normalizeAdminUserEmail(email);
  if (!login) return null;
  return (
    users.find((user) => {
      if (normalizeAdminUserEmail(user.email) === emailToken) return false;
      return deriveSimappeLoginUsername(user.email).trim().toLowerCase() === login;
    }) ?? null
  );
}

export function resolveAdminUserCreateConflictMessage(
  users: AdminSystemUser[],
  email: string,
): string | null {
  const emailConflict = findAdminUserEmailConflict(users, email);
  if (emailConflict) {
    return `Ya existe un usuario con el correo ${emailConflict.email.trim()}.`;
  }
  const loginConflict = findAdminUserLoginUsernameConflict(users, email);
  if (loginConflict) {
    const login = deriveSimappeLoginUsername(email);
    return `El usuario de acceso «${login}» ya está en uso (correo: ${loginConflict.email.trim()}).`;
  }
  return null;
}

export function resolveAdminUserPasswordApiError(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const detail = extractHttpErrorDetail(err);
  const lower = detail.toLowerCase();
  const isPasswordIssue =
    err.status === 422 ||
    lower.includes('password') ||
    lower.includes('contraseña') ||
    lower.includes('body.password');
  if (!isPasswordIssue) return null;
  if (detail.trim()) return detail;
  if (err.status === 422) {
    return 'La contraseña no cumple la política de seguridad. Revise los requisitos indicados y vuelva a intentarlo.';
  }
  return null;
}

export function resolveAdminUserGatewayApiError(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  if (err.status !== 502 && err.status !== 503 && err.status !== 504) return null;
  return (
    'El servidor Vault no respondió (error de gateway). Espere unos segundos, recargue el listado e intente de nuevo. ' +
    'Si persiste, escale a infra/backend QA.'
  );
}

export function resolveAdminUserSimappeHomologationApiError(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const detail = extractHttpErrorDetail(err);
  const lower = detail.toLowerCase();
  const simappeHint =
    lower.includes('simappe') ||
    lower.includes('homolog') ||
    lower.includes('user-identity') ||
    lower.includes('identidad');
  if (!simappeHint) return null;

  const duplicateHint =
    lower.includes('duplicate') ||
    lower.includes('duplicad') ||
    lower.includes('already exists') ||
    lower.includes('ya existe') ||
    (lower.includes('username') && lower.includes('exist')) ||
    (lower.includes('user') && lower.includes('exist'));

  if (duplicateHint || err.status === 409) {
    return (
      detail.trim() ||
      'El usuario de acceso o correo ya existe en Simappe. Revise el listado, pruebe otro correo o pida limpieza en SimappeAdmin.'
    );
  }

  if (err.status === 500 || err.status === 502) {
    return (
      detail.trim() ||
      'SimappeAdmin no pudo crear la identidad del usuario (error interno). Reintente; si falla de nuevo, escale a backend/Simappe con la hora exacta y el correo usado.'
    );
  }

  return detail.trim() || null;
}

export function resolveAdminUserDuplicateApiError(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const detail = extractHttpErrorDetail(err);
  if (err.status === 409 && detail.trim()) {
    return detail;
  }
  const lower = detail.toLowerCase();
  const duplicateHint =
    lower.includes('duplicate') ||
    lower.includes('duplicad') ||
    lower.includes('already exists') ||
    lower.includes('ya existe') ||
    lower.includes('unique constraint') ||
    (lower.includes('username') && lower.includes('exist')) ||
    (lower.includes('email') && (lower.includes('exist') || lower.includes('registr')));
  if (err.status === 409 || duplicateHint) {
    return detail.trim() || 'Ya existe un usuario con ese correo o nombre de acceso. Verifique el listado o use otro correo.';
  }
  if (err.status === 500 || err.status === 502) {
    if (duplicateHint) {
      return detail.trim() || 'El usuario ya podría existir en Simappe. Revise el listado o pruebe con otro correo/usuario de acceso.';
    }
  }
  return null;
}

export function resolveAdminUserFormApiError(err: unknown): string | null {
  // Homologación Simappe: 400/409 con `detail` se muestran tal cual (superadmin único, companyId, 409).
  if (err instanceof HttpErrorResponse && (err.status === 400 || err.status === 409)) {
    const detail = extractHttpErrorDetail(err).trim();
    if (detail) return detail;
  }
  return (
    resolveAdminUserDuplicateApiError(err) ??
    resolveAdminUserGatewayApiError(err) ??
    resolveAdminUserEmailApiError(err) ??
    resolveAdminUserPasswordApiError(err) ??
    resolveAdminUserSimappeHomologationApiError(err)
  );
}

/** Mensajes claros para DELETE /api/admin/users/{id} (alineado con back Vault). */
export function resolveAdminUserDeleteApiError(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const detail = extractHttpErrorDetail(err);
  const lower = detail.toLowerCase();

  if (err.status === 400) {
    if (lower.includes('superadmin') || lower.includes('super admin')) {
      return detail.trim() || 'No se puede eliminar un superadministrador.';
    }
    if (
      lower.includes('propio') ||
      lower.includes('propia') ||
      lower.includes('yourself') ||
      lower.includes('tu usuario') ||
      lower.includes('su propio')
    ) {
      return detail.trim() || 'No puede eliminar su propio usuario.';
    }
    return (
      detail.trim() ||
      'No se puede eliminar este usuario. Verifique que no sea su cuenta ni un superadministrador.'
    );
  }

  if (err.status === 403) {
    return (
      detail.trim() ||
      'No tiene permisos para eliminar usuarios. Se requiere rol de superadministrador.'
    );
  }

  if (err.status === 404) {
    return (
      detail.trim() ||
      'El usuario ya no existe en Vault. Pulse «Actualizar» en el listado e intente de nuevo.'
    );
  }

  const gateway = resolveAdminUserGatewayApiError(err);
  if (gateway) return gateway;

  if (detail.trim()) return detail.trim();
  return null;
}

/** Errores de alta que deben mostrarse en el campo Correo (409 Simappe/Vault). */
export function shouldAssignAdminUserFormEmailError(err: unknown): boolean {
  if (!(err instanceof HttpErrorResponse)) return false;
  if (err.status === 409) return true;
  return !!(
    resolveAdminUserEmailApiError(err) ??
    resolveAdminUserDuplicateApiError(err) ??
    resolveAdminUserSimappeHomologationApiError(err)
  );
}

export interface AdminUserSuccessFeedback {
  title: string;
  message: string;
}

/** Más tiempo que el default del UI kit (3s) para leer hints de login u otros detalles. */
export const ADMIN_USER_SUCCESS_ALERT_DURATION_MS = 8000;

export function buildAdminUserCreatedSuccessFeedback(
  accessUsername: string,
): AdminUserSuccessFeedback {
  const loginHint = buildVaultUserLoginHintFromAccessUsername(accessUsername);
  return {
    title: 'Usuario creado',
    message: loginHint || 'El usuario quedó registrado correctamente.',
  };
}

/** Create OK según back: identidad Simappe + username de acceso presentes. */
export function isAdminUserCreateProvisionComplete(
  user: Pick<AdminSystemUser, 'simappe_user_id' | 'access_username' | 'email'>,
): boolean {
  const simappeId = (user.simappe_user_id ?? '').trim();
  const access = resolveAdminUserAccessUsername(user);
  return !!simappeId && !!access;
}

export function resolveAdminUserCreateAccessUsername(
  user: Pick<AdminSystemUser, 'access_username' | 'email'>,
  fallbackUsername = '',
): string {
  return (
    resolveAdminUserAccessUsername(user) ||
    fallbackUsername.trim() ||
    deriveSimappeLoginUsername(user.email)
  );
}

export function buildAdminUserUpdatedSuccessFeedback(options: {
  assigningCompany: boolean;
  roleChanged: boolean;
}): AdminUserSuccessFeedback {
  if (options.assigningCompany) {
    return {
      title: 'Cambios guardados',
      message: 'Empresa Vault y rol asignados correctamente.',
    };
  }
  if (options.roleChanged) {
    return {
      title: 'Cambios guardados',
      message: 'Usuario y rol Vault actualizados correctamente.',
    };
  }
  return {
    title: 'Cambios guardados',
    message: 'Usuario actualizado correctamente.',
  };
}

export function buildAdminUserToggleSuccessFeedback(isActive: boolean): AdminUserSuccessFeedback {
  return isActive
    ? { title: 'Usuario activado', message: 'El usuario puede volver a acceder a Vault.' }
    : {
        title: 'Usuario desactivado',
        message: 'El usuario ya no podrá acceder a Vault hasta que lo reactive.',
      };
}

export function buildAdminUserDeletedSuccessFeedback(): AdminUserSuccessFeedback {
  return {
    title: 'Usuario eliminado',
    message:
      'El usuario fue retirado de Vault y ya no tendrá acceso al sistema.',
  };
}

export interface AdminUserConfirmDialogConfig {
  title: string;
  message: string;
  confirmLabel: string;
  variant: 'success' | 'error' | 'warning' | 'info';
}

export function buildAdminUserActivateConfirmDialog(
  user: Pick<AdminSystemUser, 'full_name'>,
): AdminUserConfirmDialogConfig {
  const name = user.full_name?.trim() || 'este usuario';
  return {
    title: 'Activar usuario',
    message: `¿Activar a ${name}? Podrá volver a iniciar sesión y acceder según sus permisos asignados.`,
    confirmLabel: 'Activar',
    variant: 'info',
  };
}

export function buildAdminUserDeactivateConfirmDialog(
  user: Pick<AdminSystemUser, 'full_name'>,
): AdminUserConfirmDialogConfig {
  const name = user.full_name?.trim() || 'este usuario';
  return {
    title: 'Desactivar usuario',
    message: `¿Desactivar a ${name}? No podrá acceder a Vault hasta que reactive su cuenta.`,
    confirmLabel: 'Desactivar',
    variant: 'warning',
  };
}

export function buildAdminUserDeleteConfirmDialog(
  user: Pick<AdminSystemUser, 'full_name'>,
): AdminUserConfirmDialogConfig {
  const name = user.full_name?.trim() || 'este usuario';
  return {
    title: 'Eliminar usuario',
    message: `¿Eliminar a ${name}? Perderá acceso a Vault. Si conserva credenciales OAuth, al intentar entrar verá acceso restringido.`,
    confirmLabel: 'Eliminar',
    variant: 'error',
  };
}

export function resolveEditableVaultTenantRole(
  tenants: AdminSystemUser['tenants'],
  fallback = 'consulta',
): string {
  for (const row of tenants ?? []) {
    const role = displayVaultTenantRole(row.role);
    if (role) {
      const norm = normalizeVaultRoleToken(row.role);
      if (VAULT_TENANT_ROLE_OPTIONS.some((o) => o.value === norm)) {
        return norm;
      }
      return row.role.trim();
    }
  }
  return fallback;
}

export function userMatchesVaultRoleFilter(user: AdminSystemUser, roleFilter: string): boolean {
  if (user.is_superadmin) return false;
  const want = normalizeVaultRoleToken(roleFilter);
  if (!want) return true;
  for (const row of user.tenants ?? []) {
    const norm = normalizeVaultRoleToken(row.role);
    if (norm && !isSimappeTechnicalRoleToken(norm) && norm === want) return true;
  }
  return false;
}

export function userMatchesSearchTerm(user: AdminSystemUser, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const email = user.email.toLowerCase();
  const access = resolveAdminUserAccessUsername(user).toLowerCase();
  const at = email.indexOf('@');
  const loginUsername = at > 0 ? email.slice(0, at) : email;
  if (
    user.full_name.toLowerCase().includes(q) ||
    email.includes(q) ||
    access.includes(q) ||
    loginUsername.includes(q)
  ) {
    return true;
  }
  const company = formatAdminUserCompanySummary(user).toLowerCase();
  const role = formatAdminUserRoleSummary(user).toLowerCase();
  return company.includes(q) || role.includes(q);
}

const VAULT_USER_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const HIDDEN_ADMIN_USERS_KEY = 'vault_admin_hidden_user_ids';
const HIDDEN_ADMIN_USER_EMAILS_KEY = 'vault_admin_hidden_user_emails';

/** @deprecated Usar `resolveCanonicalVaultSuperadminEmail()` de `@nebula/shared-vault/data-access`. */
export const CANONICAL_VAULT_SUPERADMIN_EMAIL = 'dev@datavault.com';

export {
  resolveCanonicalVaultSuperadminEmail,
  emailIsCanonicalVaultSuperadmin,
} from '@nebula/shared-vault/data-access';

export function normalizeAdminUserEmail(email: string | undefined | null): string {
  return (email ?? '').trim().toLowerCase();
}

export function isCanonicalVaultSuperadmin(user: Pick<AdminSystemUser, 'email'>): boolean {
  return emailIsCanonicalVaultSuperadmin(user.email);
}

/** Cuentas legacy de la arquitectura anterior que deben ocultarse de la UI. */
const LEGACY_VAULT_ADMIN_EMAILS = new Set([
  'desarrollodatavault@gmail.com',
]);

/** Superadmin efectivo para listado y acciones (el API legacy puede marcar cuentas incorrectas). */
export function resolveAdminUserSuperadminForUi(user: AdminSystemUser): boolean {
  return isCanonicalVaultSuperadmin(user);
}

export function normalizeAdminUserForVaultUi(user: AdminSystemUser): AdminSystemUser {
  return {
    ...user,
    is_superadmin: resolveAdminUserSuperadminForUi(user),
  };
}

export function canDeleteAdminUser(user: AdminSystemUser): boolean {
  return !isCanonicalVaultSuperadmin(user);
}

function adminUsersPersistStorage(): Storage | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage;
}

function migrateSessionHiddenAdminUsersToLocal(): void {
  if (typeof sessionStorage === 'undefined' || typeof localStorage === 'undefined') return;
  const sessionRaw = sessionStorage.getItem(HIDDEN_ADMIN_USERS_KEY);
  if (!sessionRaw) return;
  try {
    const sessionParsed = JSON.parse(sessionRaw) as unknown;
    if (!Array.isArray(sessionParsed)) return;
    const merged = new Set(readHiddenAdminUserIds());
    for (const id of sessionParsed) {
      if (typeof id === 'string' && id.trim()) merged.add(id.trim());
    }
    localStorage.setItem(HIDDEN_ADMIN_USERS_KEY, JSON.stringify([...merged]));
    sessionStorage.removeItem(HIDDEN_ADMIN_USERS_KEY);
  } catch {
    /* ignore */
  }
}

function readHiddenStringSet(key: string): Set<string> {
  migrateSessionHiddenAdminUsersToLocal();
  const storage = adminUsersPersistStorage();
  if (!storage) return new Set();
  try {
    const raw = storage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map((value) => value.trim()),
    );
  } catch {
    return new Set();
  }
}

function writeHiddenStringSet(key: string, values: Set<string>): void {
  const storage = adminUsersPersistStorage();
  if (!storage) return;
  storage.setItem(key, JSON.stringify([...values]));
}

/** Usuarios de la arquitectura anterior (id numérico, no UUID o email legacy) incompatibles con Vault nuevo. */
export function isLegacyVaultAdminUser(user: AdminSystemUser): boolean {
  if (isCanonicalVaultSuperadmin(user)) return false;
  const email = normalizeAdminUserEmail(user.email);
  if (LEGACY_VAULT_ADMIN_EMAILS.has(email)) return true;
  const id = (user.id ?? '').trim();
  if (!id) return true;
  if (/^\d+$/.test(id)) return true;
  return !VAULT_USER_UUID_RE.test(id);
}

/** Usuario migrado sin rol Vault asignado (restos de la bóveda anterior). */
export function isStaleVaultAdminUser(
  user: AdminSystemUser,
  catalog?: Array<Pick<AdminTenantSummary, 'id' | 'name'>>,
): boolean {
  if (isCanonicalVaultSuperadmin(user)) return false;
  return formatAdminUserRolesSummary(user, catalog) === ADMIN_USER_UNASSIGNED_LABEL;
}

/** Usuarios que deben ocultarse automáticamente al cargar la lista. */
export function shouldAutoHideAdminUser(
  user: AdminSystemUser,
  catalog?: Array<Pick<AdminTenantSummary, 'id' | 'name'>>,
): boolean {
  if (isCanonicalVaultSuperadmin(user)) return false;
  return isLegacyVaultAdminUser(user) || isStaleVaultAdminUser(user, catalog);
}

export function readHiddenAdminUserIds(): Set<string> {
  return readHiddenStringSet(HIDDEN_ADMIN_USERS_KEY);
}

export function readHiddenAdminUserEmails(): Set<string> {
  return readHiddenStringSet(HIDDEN_ADMIN_USER_EMAILS_KEY);
}

export function hideAdminUserId(userId: string): void {
  const id = (userId ?? '').trim();
  if (!id) return;
  const hidden = readHiddenAdminUserIds();
  hidden.add(id);
  writeHiddenStringSet(HIDDEN_ADMIN_USERS_KEY, hidden);
}

export function hideAdminUserEmail(email: string): void {
  const normalized = normalizeAdminUserEmail(email);
  if (!normalized) return;
  const hidden = readHiddenAdminUserEmails();
  hidden.add(normalized);
  writeHiddenStringSet(HIDDEN_ADMIN_USER_EMAILS_KEY, hidden);
}

export function hideAdminUserFromList(user: Pick<AdminSystemUser, 'id' | 'email'>): void {
  if (isCanonicalVaultSuperadmin(user)) return;
  hideAdminUserId(user.id);
  hideAdminUserEmail(user.email);
}

/** Revierte ocultado persistido cuando el usuario vuelve a ser válido en el listado. */
export function unhideAdminUserFromList(user: Pick<AdminSystemUser, 'id' | 'email'>): void {
  const id = (user.id ?? '').trim();
  const email = normalizeAdminUserEmail(user.email);
  const hiddenIds = readHiddenAdminUserIds();
  const hiddenEmails = readHiddenAdminUserEmails();
  let changed = false;
  if (id && hiddenIds.delete(id)) changed = true;
  if (email && hiddenEmails.delete(email)) changed = true;
  if (!changed) return;
  writeHiddenStringSet(HIDDEN_ADMIN_USERS_KEY, hiddenIds);
  writeHiddenStringSet(HIDDEN_ADMIN_USER_EMAILS_KEY, hiddenEmails);
}

/** Quita el superadmin canónico de listas ocultas persistidas (QA/dev paridad). */
export function unhideCanonicalVaultSuperadminFromAdminLists(): void {
  const canonical = normalizeAdminUserEmail(resolveCanonicalVaultSuperadminEmail());
  if (!canonical) return;

  const hiddenEmails = readHiddenAdminUserEmails();
  let emailsChanged = false;
  if (hiddenEmails.delete(canonical)) emailsChanged = true;

  const hiddenIds = readHiddenAdminUserIds();
  let idsChanged = false;
  const sessionId = vaultCurrentUserIdFromSession().trim();
  if (sessionId && hiddenIds.delete(sessionId)) idsChanged = true;

  if (emailsChanged) {
    writeHiddenStringSet(HIDDEN_ADMIN_USER_EMAILS_KEY, hiddenEmails);
  }
  if (idsChanged) {
    writeHiddenStringSet(HIDDEN_ADMIN_USERS_KEY, hiddenIds);
  }
}

export interface AdminUserListVisibilityOptions {
  /** Superadmin: muestra todos los usuarios del API; solo oculta eliminados manualmente. */
  globalScope?: boolean;
}

/**
 * Si el API no devuelve al superadmin del ambiente, lo sintetiza desde la sesión actual
 * (paridad dev → qa cuando el back aún no lista `qa@datavault.com`).
 */
export function ensureCanonicalSuperadminInAdminUserList(
  users: AdminSystemUser[],
  catalog?: Array<Pick<AdminTenantSummary, 'id' | 'name'>>,
  options?: AdminUserListVisibilityOptions,
): AdminSystemUser[] {
  unhideCanonicalVaultSuperadminFromAdminLists();
  const visible = filterVisibleAdminUsers(users, catalog, options);
  if (visible.some((u) => isCanonicalVaultSuperadmin(u))) return visible;

  const sessionEmail = readSessionUserEmail();
  if (!emailIsCanonicalVaultSuperadmin(sessionEmail)) return visible;

  const synthetic = buildCanonicalSuperadminUserFromSession();
  if (!synthetic) return visible;
  return [synthetic, ...visible];
}

function buildCanonicalSuperadminUserFromSession(): AdminSystemUser | null {
  const email = readSessionUserEmail();
  if (!emailIsCanonicalVaultSuperadmin(email)) return null;

  let id = vaultCurrentUserIdFromSession();
  if (!id) {
    try {
      const raw = localStorage.getItem('user_data');
      if (raw) {
        const user = JSON.parse(raw) as Record<string, unknown>;
        id = String(user['id'] ?? user['vault_user_id'] ?? user['user_id'] ?? '').trim();
      }
    } catch {
      /* ignore */
    }
  }
  if (!id) id = email;

  let full_name = email;
  try {
    const raw = localStorage.getItem('user_data');
    if (raw) {
      const user = JSON.parse(raw) as Record<string, unknown>;
      full_name = String(user['full_name'] ?? user['fullName'] ?? user['name'] ?? email).trim();
    }
  } catch {
    /* ignore */
  }

  return normalizeAdminUserForVaultUi({
    id,
    email,
    full_name,
    is_active: true,
    is_superadmin: true,
    tenants: [],
  });
}

export function filterVisibleAdminUsers(
  users: AdminSystemUser[],
  catalog?: Array<Pick<AdminTenantSummary, 'id' | 'name'>>,
  options?: AdminUserListVisibilityOptions,
): AdminSystemUser[] {
  unhideCanonicalVaultSuperadminFromAdminLists();
  const hiddenIds = readHiddenAdminUserIds();
  const hiddenEmails = readHiddenAdminUserEmails();
  return users.filter((u) => {
    if (isCanonicalVaultSuperadmin(u)) return true;
    if (!options?.globalScope && shouldAutoHideAdminUser(u, catalog)) return false;
    if (hiddenIds.has(u.id)) return false;
    if (hiddenEmails.has(normalizeAdminUserEmail(u.email))) return false;
    return true;
  });
}

export function canActorEditUserTenantAssignment(
  actor: VaultAdminUsersActor,
  params: {
    targetUserId: string;
    currentUserId: string;
    assignmentRole: string;
  },
): boolean {
  if (actor === 'superadmin') return true;
  if (actor !== 'admin_empresa') return false;
  if (params.targetUserId === params.currentUserId) return false;
  return true;
}

export function canActorOpenUserPermissions(
  actor: VaultAdminUsersActor,
  user: { id: string; is_superadmin: boolean; tenants?: Array<{ tenant_id: string }> },
  currentTenantId: string,
): boolean {
  if (user.is_superadmin) return false;
  if (actor === 'superadmin') return true;
  if (actor !== 'admin_empresa' || !currentTenantId) return false;
  return user.tenants?.some((t) => t.tenant_id === currentTenantId) ?? false;
}
